import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, OptionTypeBase} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNil} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';

import {TestIdProps} from 'shared/web';

import {VehicleFinancingCardForm} from './components/VehicleFinancingCardForm';

interface VehicleFinancingCardProps extends TestIdProps {
  vehicleId?: string;
  isReadonly?: boolean;
  isWithSubmit?: boolean;
}

const LEASING_CODELIST_ID = 'leasing_and_credit_company';

/**
 * Resolves the sale vehicle and hands it to the form only once it is available.
 *
 * The nil handling must stay in this component and out of the form: `apps/dms` builds with React
 * Compiler enabled, which lifts property reads out of event handlers into render-time dependency
 * checks in the component body. A nullable value deref'd inside a handler therefore crashes during
 * render, and neither `Show` nor `DataStatus` can prevent it, because both receive their children
 * as an already-constructed element. Keeping the query here and passing a non-optional
 * `saleVehicle` down makes that class of crash impossible to reintroduce without a type error.
 */
export function VehicleFinancingCard(props: VehicleFinancingCardProps) {
  const {
    data: saleVehicle,
    isLoading: isSaleVehicleLoading,
    isError: isSaleVehicleError,
  } = useGetSaleVehicleQuery(isNotNil(props.vehicleId) ? {vehicleId: props.vehicleId} : skipToken);

  const {
    data: leasingAndCreditCompanies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useReadCodelistQuery({
    codelistId: LEASING_CODELIST_ID,
  });

  const institutionOptions: OptionTypeBase<string>[] =
    leasingAndCreditCompanies?.codes
      ?.filter((code) => !code.isDisabled)
      .map((code) => ({
        label: code.name,
        value: code.codeId,
      })) ?? [];

  return (
    <Show when={isNotNil(props.vehicleId)}>
      <DataStatus
        data={saleVehicle}
        isLoading={isSaleVehicleLoading || isCompaniesLoading}
        isError={isSaleVehicleError || isCompaniesError}
      >
        {({data}) => (
          <VehicleFinancingCardForm
            saleVehicle={data}
            institutionOptions={institutionOptions}
            isReadonly={props.isReadonly}
            isWithSubmit={props.isWithSubmit}
            data-testid={props['data-testid']}
          />
        )}
      </DataStatus>
    </Show>
  );
}
